import { getCookie, setCookie } from "./common/ts-utils"
import { getCurrentLanguage } from "./i18n/utils"
import { setAmplitudeOptOut } from "./common/amplitude-event-logging"
import { hasTrackingConsent, parseOptOutMulti } from "./common/tracking-consent"
import { tCfpMountOptions, tUpdateGdprMessageEvent, tUpdateGdprPayload } from "./cookieConsent.types"
import { unsetIntercomUser } from "./common/intercom-event-logging"

export { hasTrackingConsent }

const OPTOUTMULTI_COOKIE = "OPTOUTMULTI"
const OPTOUTMULTI_TYPE_COOKIE = "OPTOUTMULTI_TYPE"
const OPTOUTMULTI_GEO_COOKIE = "OPTOUTMULTI_GEO"
const COOKIE_MAX_AGE_DAYS = 365

// opted-in defaults for every category: 0 = opted in, 1 = opted out
const PASSIVE_OPTOUTMULTI = "0:0|0:0|c1:0|c7:0|c8:0|c9:0"

// FullStory/Amplitude cookies to clear when a user opts out of tracking
const TRACKING_COOKIE_PREFIXES = ["fs_", "_fs", "amplitude_", "amp_"]

/**
 * Write the non-GDPR passive-consent cookies (OPTOUTMULTI, OPTOUTMULTI_TYPE=P, OPTOUTMULTI_GEO)
 * so trackers are gated on `OPTOUTMULTI` from the very first page load, with no banner shown.
 * No-ops if the user already made an explicit choice (OPTOUTMULTI_TYPE=A), so a later page
 * load never clobbers an active decision made via Cookie Preferences.
 */
export const applyPassiveConsent = (countryCode = "US"): void => {
    if (getCookie(OPTOUTMULTI_TYPE_COOKIE) === "A") return

    setCookie(OPTOUTMULTI_COOKIE, PASSIVE_OPTOUTMULTI, "/", COOKIE_MAX_AGE_DAYS)
    setCookie(OPTOUTMULTI_TYPE_COOKIE, "P", "/", COOKIE_MAX_AGE_DAYS)
    setCookie(OPTOUTMULTI_GEO_COOKIE, countryCode, "/", COOKIE_MAX_AGE_DAYS)
}

let cfpScriptsLoaded: Promise<void> | undefined

const loadScript = (src: string): Promise<void> =>
    new Promise((resolve, reject) => {
        const script = document.createElement("script")
        script.src = src
        script.onload = () => resolve()
        script.onerror = () => reject(new Error(`Failed to load script: ${src}`))
        document.head.appendChild(script)
    })

/**
 * Lazy-load the CFP vendor, runtime, and MFE scripts, in order. Only called once; subsequent
 * calls return the same in-flight/completed promise.
 */
export const loadCfpScripts = (): Promise<void> => {
    if (!cfpScriptsLoaded) {
        cfpScriptsLoaded = loadScript(window.cfp_vendor_script_url)
            .then(() => loadScript(window.cfp_runtime_script_url))
            .then(() => loadScript(window.cfp_mfe_script_url))
    }
    return cfpScriptsLoaded
}

const optOutSelectionFor = (value: string | undefined): "yes" | "no" | "" => {
    if (value === "0") return "yes"
    if (value === "1") return "no"
    return ""
}

/**
 * The MFE's AEM content fragments are keyed by region-qualified locale (e.g. "en-US"), not the
 * bare language code ("en") the app's i18n setup uses -- a bare code 404s the content fetch
 * (CFP_CONT_0005). Pair the user's selected app language with the region from the browser's
 * own locale (`navigator.language`), falling back to "US" only if the browser doesn't report
 * a region-qualified locale at all.
 */
const getCfpLocale = (): string => {
    const region = navigator.language?.split("-")[1] ?? "US"
    return `${getCurrentLanguage()}-${region}`
}

/**
 * The MFE reads `window.utag_data.locale` to fetch its localized AEM content, a Tealium global
 * it assumes is already present even for hosts (like Rhumbix) that don't load Tealium. Stub it
 * so that read doesn't throw; leaves any real Tealium data (locale included) untouched if present.
 */
const ensureUtagData = (): void => {
    window.utag_data = { locale: getCfpLocale(), ...window.utag_data }
}

let cfpEnvironmentInitialized = false

/**
 * Load the CFP scripts (if needed) and mount the CFP Privacy Banner MFE in settings mode,
 * so the user can review/change their consent choices.
 */
export const openCookiePreferences = async (): Promise<void> => {
    if (!window.cfp_vendor_script_url || !window.cfp_runtime_script_url || !window.cfp_mfe_script_url) {
        console.error("[Cookie Consent] CFP script URLs are not configured; cannot open preferences")
        return
    }

    try {
        await loadCfpScripts()
    } catch (error) {
        console.error("[Cookie Consent] Failed to load CFP scripts", error)
        return
    }

    ensureUtagData()

    // The CFP environment provider errors if initialized more than once, so only call it
    // the first time the banner is opened.
    if (!cfpEnvironmentInitialized) {
        window.cfp?.providers.environment.init(window.rmbx_env === "prod" ? "prd" : "stg")
        cfpEnvironmentInitialized = true
    }

    const current = parseOptOutMulti(getCookie(OPTOUTMULTI_COOKIE))
    const options: tCfpMountOptions = {
        initScreen: "settings",
        initContinueButtonUse: true,
        initImproveSelection: optOutSelectionFor(current.c8),
        initCustomizeSelection: optOutSelectionFor(current.c9),
        locale: getCfpLocale(),
    }
    const wrapper = document.getElementById("adsk-gdpr-footer-wrapper")
    if (!wrapper) {
        console.error("[Cookie Consent] Missing #adsk-gdpr-footer-wrapper; cannot mount CFP settings banner")
        return
    }
    window.cfp?.providers.integration.mount(window.cfp.mfe.gdprConsent, wrapper, options)
}

/**
 * Immediately stop FullStory/Amplitude/Intercom tracking and clear their cookies.
 * There is no equivalent "start tracking now" path — since these boot unconditionally on
 * page load, re-consenting takes effect on the user's next page refresh instead.
 */
const stopTrackingIntegrations = (): void => {
    window.FS?.shutdown()
    setAmplitudeOptOut(true)
    unsetIntercomUser()

    // Cookies set by third-party SDKs (FullStory, Amplitude) are usually scoped with a
    // `domain` attribute (e.g. `.rhumbix.com`), and a clear only takes effect when the
    // `domain`/`path` match the original cookie exactly. Try every domain variant a
    // same-site SDK could plausibly have used.
    const hostParts = window.location.hostname.split(".")
    const domains = [undefined, window.location.hostname]
    if (hostParts.length > 2) domains.push(`.${hostParts.slice(-2).join(".")}`)

    TRACKING_COOKIE_PREFIXES.forEach(prefix => {
        document.cookie.split(";").forEach(cookie => {
            const name = cookie.split("=")[0].trim()
            if (name.startsWith(prefix)) {
                domains.forEach(domain => {
                    const domainAttr = domain ? `; domain=${domain}` : ""
                    document.cookie = `${name}=; path=/${domainAttr}; expires=Thu, 01 Jan 1970 00:00:01 GMT`
                })
            }
        })
    })
}

/**
 * Merge an `updateGdpr` payload into the current OPTOUTMULTI cookie's values, then write the
 * updated cookies and stop tracking if the user just opted out.
 */
export const applyGdprUpdate = (update: tUpdateGdprPayload): void => {
    // Trackers are only ever started (in index.jsx) if consent already existed at page load, so
    // that's the only case where there's anything running for us to stop.
    const wasTracking = hasTrackingConsent()
    const current = parseOptOutMulti(getCookie(OPTOUTMULTI_COOKIE))
    const merged = { c1: "0", c7: "0", c8: "0", c9: "0", ...current, ...update }
    const optoutMulti = ["0:0", "0:0", `c1:${merged.c1}`, `c7:${merged.c7}`, `c8:${merged.c8}`, `c9:${merged.c9}`].join("|")

    setCookie(OPTOUTMULTI_COOKIE, optoutMulti, "/", COOKIE_MAX_AGE_DAYS)
    setCookie(OPTOUTMULTI_TYPE_COOKIE, "A", "/", COOKIE_MAX_AGE_DAYS)

    // If someone opts-out, we stop tracking immediately, but re-starting trackers is more difficult
    // so we don't do the opposite.  If someone opts-in we don't start tracking them until the next
    // full refresh.
    if (merged.c8 === "1" && wasTracking) {
        stopTrackingIntegrations()
    }
}

/**
 * Listen for `updateGdpr` postMessage events sent by the CFP Privacy Banner MFE and apply them.
 * Returns an unsubscribe function.
 */
export const subscribeToGdprUpdates = (): (() => void) => {
    const listener = (event: tUpdateGdprMessageEvent): void => {
        if (event.origin !== window.location.origin) return
        if (!event.data?.updateGdpr) return
        applyGdprUpdate(event.data.updateGdpr)
    }
    window.addEventListener("message", listener as EventListener)
    return () => window.removeEventListener("message", listener as EventListener)
}

export const initializeAdpConversionSdk = async (): Promise<void> => {
    if (typeof window.Adp === "function" && window.CjsTracker) {
        try {
            await window.CjsTracker.init(window.Adp())
                .setEnv(window.rmbx_env === "prod" ? "prd" : "stg")
                // to enable auto capture link click
                .trackClickElements()
                .defaultEventAttributes({ event_generator: "rmbxcl" })
                // set default  data
                .defaultEventData({
                    page: {
                        page_category: "product",
                        // E.g. en-US
                        page_locale: getCfpLocale(),
                    },
                })
                .load({ id: window.forge_client_id })
        } catch (error) {
            console.error("[CJS] Error initializing SDK:", error)
            throw error
        }
    } else {
        console.debug("[CJS] Adp() function is not available.")
    }
}
